// landingAdapter — parse the `howItLands` reply at the boundary
// (`Packaging.Landing`). Pure. Every case the server names is read
// explicitly; anything else lands on one default: an unknown fate is "not
// sent" under the block's kind (never shown as arriving), an unknown
// reshape is `unknown`, and a proposal kind this client cannot act on is
// left out (it would have no buttons). A reply with no format is malformed.

export { landingFormatId, parseLanding };

import { exhaustSwitchCase, Result } from "@bilda/utils";
import { type ExternalError, mkMalformed } from "./externalError.js";
import type {
    BlockFate,
    Landing,
    LandingFate,
    LandingFormat,
    LandingPlace,
    LandingReshape,
    MoveTextProposal,
} from "./ports/landing.js";
import { fields, list, str } from "./wireShape.js";

// The export format id a landing format is asked by.
const landingFormatId = (format: LandingFormat): string => {
    switch (format) {
        case "torus":
            return "F_torus";
        case "canvas":
            return "F_canvas";
        default:
            return exhaustSwitchCase(format);
    }
};

const placeOf = (raw: unknown): LandingPlace | undefined => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    const part = str(f?.get("part"));
    switch (type) {
        case "first":
            return { type };
        case "before":
        case "after":
            return part === undefined ? undefined : { type, part };
        default:
            return undefined;
    }
};

const reshapeOf = (raw: unknown): LandingReshape => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    switch (type) {
        case "page_of_its_own": {
            const title = str(f?.get("title"));
            const place = placeOf(f?.get("place"));
            return title === undefined || place === undefined
                ? { type: "unknown" }
                : { type, title, place };
        }
        case "text_header": {
            const within = str(f?.get("within"));
            return within === undefined
                ? { type: "unknown" }
                : { type, within };
        }
        case "quiz_after_page": {
            const page = str(f?.get("page"));
            return page === undefined ? { type: "unknown" } : { type, page };
        }
        case "ordinary_text":
        case "module_of_its_own":
        case "page_and_quiz":
        case "name_only":
            return { type };
        default:
            return { type: "unknown" };
    }
};

const fateOf = (raw: unknown, kind: string): LandingFate => {
    const f = fields(raw);
    switch (str(f?.get("type"))) {
        case "kept":
            return { type: "kept" };
        case "reshaped":
            return { type: "reshaped", into: reshapeOf(f?.get("into")) };
        case "not_sent":
            return {
                type: "not_sent",
                kind: str(fields(f?.get("why"))?.get("kind")) ?? kind,
            };
        default:
            return { type: "not_sent", kind };
    }
};

const blockFateOf = (raw: unknown): readonly BlockFate[] => {
    const f = fields(raw);
    const owner = str(f?.get("owner"));
    const node = str(f?.get("node"));
    const kind = str(f?.get("kind")) ?? "unknown";
    const part = str(f?.get("part"));
    return owner === undefined || node === undefined
        ? []
        : [
              {
                  owner,
                  node,
                  kind,
                  ...(part === undefined ? {} : { part }),
                  fate: fateOf(f?.get("fate"), kind),
              },
          ];
};

const proposalOf = (raw: unknown): readonly MoveTextProposal[] => {
    const f = fields(raw);
    const container = str(f?.get("container"));
    const part = str(f?.get("part"));
    const partTitle = str(f?.get("partTitle"));
    const blocks = list(f?.get("blocks")).flatMap((b) => str(b) ?? []);
    return str(f?.get("type")) === "move_text_into_part" &&
        container !== undefined &&
        part !== undefined &&
        partTitle !== undefined &&
        blocks.length > 0
        ? [{ container, blocks, part, partTitle }]
        : [];
};

const formatOf = (raw: unknown): LandingFormat | undefined => {
    switch (str(raw)) {
        case "torus":
            return "torus";
        case "canvas":
            return "canvas";
        default:
            return undefined;
    }
};

const parseLanding = (raw: unknown): Result.Result<Landing, ExternalError> => {
    const f = fields(raw);
    const format = formatOf(f?.get("format"));
    return format === undefined
        ? Result.err(mkMalformed("howItLands returned no format"))
        : Result.ok({
              format,
              fates: list(f?.get("fates")).flatMap(blockFateOf),
              proposals: list(f?.get("proposals")).flatMap(proposalOf),
          });
};
